import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from './supabaseClient';
import SEO from './SEO';
import './BlogDetail.css';

const BlogDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams(); // This will now be the slug
  const navigate = useNavigate();
  const location = useLocation();
  const [blog, setBlog] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Handle back navigation - go to home if direct visit, otherwise go back
  const handleGoBack = () => {
    // Check if there's history to go back to
    if (location.key !== 'default') {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  useEffect(() => {
    fetchBlog();
  }, [id]);

  const fetchBlog = async () => {
    try {
      setIsLoading(true);

      // Try to fetch by slug first, then fallback to UUID for backwards compatibility
      let query = supabase.from('blogs').select('*');

      // Check if it's a UUID (contains hyphens and is 36 chars) or a slug
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

      if (isUUID) {
        query = query.eq('id', id);
      } else {
        query = query.eq('slug', id);
      }

      const { data, error } = await query.single();

      if (error) throw error;
      setBlog(data);
    } catch (error) {
      console.error('Error fetching blog:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="blog-detail-page">
        <div className="blog-detail-loading">
          <div className="loading-spinner"></div>
          <p>{t('blogDetail.loading')}</p>
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="blog-detail-page">
        <div className="blog-detail-error">
          <h2>{t('blogDetail.notFound')}</h2>
          <p>{t('blogDetail.notFoundText')}</p>
          <button className="btn-back" onClick={handleGoBack}>
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {t('blogDetail.back')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-detail-page">
      <SEO
        title={`${blog.title} | Reveel Blog`}
        description={blog.description || blog.title}
        url={`https://reveel.health/blog/${blog.slug || blog.id}`}
        image={blog.image_url || 'https://lllqncjwqcbrygqujdin.supabase.co/storage/v1/object/public/media/logo512.png'}
        type="article"
        keywords="emotional wellness, mental health, mindfulness, self-improvement, biofeedback"
      />
      {/* Floating Particles */}
      <div className="blog-particles">
        <div className="blog-particle bp1"></div>
        <div className="blog-particle bp2"></div>
        <div className="blog-particle bp3"></div>
      </div>

      <div className="blog-detail-container">
        {/* Back Button */}
        <button className="btn-back-home" onClick={handleGoBack}>
          <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          {t('blogDetail.back')}
        </button>

        {/* Blog Header */}
        <div className="blog-detail-header">
          <p className="blog-detail-tag">{t('blogDetail.tag')}</p>
          <h1 className="blog-detail-title">
            {blog.title}
          </h1>
          <div className="blog-detail-meta">
            <div className="meta-item">
              <svg className="meta-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 6v6l4 2"/>
              </svg>
              <span className="blog-date">
                {new Date(blog.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </span>
            </div>
          </div>
        </div>

        {/* Blog Image */}
        {blog.image_url && (
          <div className="blog-detail-image-wrapper">
            <div className="image-glow"></div>
            <div className="blog-detail-image">
              <img src={blog.image_url} alt={blog.title} />
            </div>
          </div>
        )}

        {/* Blog Content */}
        <div className="blog-detail-content">
          <div className="content-glow"></div>
          {blog.content ? (
            <div className="blog-content-text">
              {blog.content.split('\n').map((paragraph, index) => {
                // Handle headers
                if (paragraph.startsWith('# ')) {
                  return <h1 key={index} className="content-h1">{paragraph.replace('# ', '')}</h1>;
                }
                if (paragraph.startsWith('## ')) {
                  return <h2 key={index} className="content-h2">{paragraph.replace('## ', '')}</h2>;
                }
                if (paragraph.startsWith('### ')) {
                  return <h3 key={index} className="content-h3">{paragraph.replace('### ', '')}</h3>;
                }
                // Handle bold text
                if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                  return <p key={index} className="content-bold">{paragraph.replace(/\*\*/g, '')}</p>;
                }
                // Handle list items
                if (paragraph.startsWith('- ')) {
                  return <li key={index} className="content-list-item">{paragraph.replace('- ', '')}</li>;
                }
                // Handle empty lines
                if (paragraph.trim() === '') {
                  return <br key={index} />;
                }
                // Regular paragraphs
                return <p key={index} className="content-paragraph">{paragraph}</p>;
              })}
            </div>
          ) : (
            <p className="blog-detail-description">{blog.description}</p>
          )}
        </div>

        {/* Navigation Footer */}
        <div className="blog-detail-footer">
          <button className="btn-back-footer" onClick={handleGoBack}>
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {t('blogDetail.back')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BlogDetail;